import React from 'react';
import { styled } from '@this/constants/themes';
import { Link } from '@this/shared/ui/navigations/link';
import Notification from '@this/src/notification';
import PdfIcon from '@material-ui/icons/PictureAsPdf';
import type TransportElementAttachFile from '@this/src/domain/transport_element_attach_file';
import type TravelerInformation from '@this/src/domain/traveler_information';
import type ElementAttachFile from '@this/domain/element_attach_file';

type AttachFile = TransportElementAttachFile | ElementAttachFile;

type Props = {
  className?: string;
  attachFiles?: AttachFile[];
  travelerInformations: TravelerInformation[];
  attachableType?: string;
  attachableId?: number;
  onChange: (file: File, transportElementId: number, travelerInformationId: string | null) => void;
  onDelete?: (transportElementId: number, travelerInformationId: string | null, index: number) => void;
};

export const TransportElementAttachFileForm = ({
  className,
  attachFiles = [],
  travelerInformations,
  attachableType,
  attachableId,
  onChange,
  onDelete
}: Props) => {
  const [localAttachFiles, setLocalAttachFiles] = React.useState(() => {
    return attachFiles.reduce((acc, file) => {
      const travelerId = file.travelerInformationId?.toString() ?? 'null';
      if (!acc[travelerId]) acc[travelerId] = [];
      acc[travelerId].push(file);
      return acc;
    }, {} as { [travelerId: string]: AttachFile[] });
  });

  const [selectedTravelerId, setSelectedTravelerId] = React.useState<string | null>(
    travelerInformations[0]?.id?.toString() ?? null
  );

  const handleAddInputField = () => {
    const idKey = selectedTravelerId ?? 'null';
    const newPlaceholder = {
      travelerInformationId: selectedTravelerId,
      uploadFileName: '',
      isPlaceholder: true
    } as unknown as AttachFile;

    setLocalAttachFiles(prev => ({
      ...prev,
      [idKey]: [...(prev[idKey] || []), newPlaceholder]
    }));
  };

  const handleRemoveInputField = (travelerId: string | null, index: number) => {
    const idKey = travelerId ?? 'null';
    setLocalAttachFiles(prev => {
      const updated = { ...prev };
      updated[idKey] = updated[idKey]?.filter((_, i) => i !== index);
      return updated;
    });

    if (onDelete && attachableId !== undefined) {
      onDelete(attachableId, travelerId === 'null' ? null : travelerId, index);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, travelerId: string, index: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      const errorMessage = 'ファイルサイズが大きすぎます。10MB以下のファイルを選択してください。';
      e.target.value = '';
      Notification.error(errorMessage);
      return;
    }

    const idKey = travelerId ?? 'null';
    setLocalAttachFiles(prev => {
      const updated = { ...prev };
      updated[idKey][index] = {
        travelerInformationId: travelerId,
        uploadFileName: file.name,
        file
      } as unknown as AttachFile;
      return updated;
    });

    if (attachableId !== undefined) {
      onChange(file, attachableId, travelerId);
    }
  };

  const generateDownloadPath = (type?: string) => (file: AttachFile) => {
    const pathMap: Record<string, string> = {
      wifi: 'element_attach_files',
      transport: 'transport_element_attach_files'
    };
    const dir = pathMap[type || 'transport'] ?? 'transport_element_attach_files';
    return `/arrangement/trips/${file.tripId}/${dir}/${file.id}.pdf`;
  };

  const downloadPath = generateDownloadPath(attachableType);

  return (
    <Root className={className}>
      <div style={{ marginBottom: '5px' }}>
        <select value={selectedTravelerId ?? ''} onChange={e => setSelectedTravelerId(e.target.value || null)}>
          <option value="null">未割り当て</option>
          {travelerInformations.map((traveler: TravelerInformation) => (
            <option key={traveler.id} value={traveler.id}>
              {traveler.lastNameKana} {traveler.firstNameKana}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleAddInputField}
          style={{
            background: 'none',
            border: 'none',
            color: '#007bff',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: 0,
            marginLeft: '10px'
          }}
        >
          ファイルを追加
        </button>
      </div>

      {travelerInformations.map((traveler: TravelerInformation) => {
        const travelerId = traveler.id.toString();
        return (
          <div key={traveler.id} style={{ marginBottom: '20px' }}>
            <h4>
              {traveler.lastNameKana} {traveler.firstNameKana}
            </h4>
            {localAttachFiles[travelerId]?.map((file, index) => (
              <FileSection key={index}>
                {(file as any).isPlaceholder ? (
                  <input
                    type="file"
                    name="files[]"
                    onChange={e => handleChange(e, travelerId, index)}
                    accept="application/pdf"
                  />
                ) : file.filePath ? (
                  <>
                    <PdfIcon style={{ marginRight: '5px' }} />
                    <Link href={downloadPath(file)} target="_blank" rel="noopener noreferrer" isExternal>
                      {file.uploadFileName}
                    </Link>
                  </>
                ) : (
                  <>
                    <PdfIcon style={{ marginRight: '5px' }} />
                    {file.uploadFileName}
                  </>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveInputField(travelerId, index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0
                  }}
                >
                  削除
                </button>
              </FileSection>
            ))}
          </div>
        );
      })}

      <div style={{ marginBottom: '20px' }}>
        <h4>出張者未割り当て</h4>
        {localAttachFiles.null?.map((file, index) => (
          <FileSection key={index}>
            {(file as any).isPlaceholder ? (
              <input
                type="file"
                name="files[]"
                onChange={e => handleChange(e, 'null', index)}
                accept="application/pdf"
              />
            ) : file.filePath ? (
              <>
                <PdfIcon style={{ marginRight: '5px' }} />
                <Link href={downloadPath(file)} target="_blank" rel="noopener noreferrer" isExternal>
                  {file.uploadFileName}
                </Link>
              </>
            ) : (
              <>
                <PdfIcon style={{ marginRight: '5px' }} />
                {file.uploadFileName}
              </>
            )}
            <button
              type="button"
              onClick={() => handleRemoveInputField('null', index)}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              削除
            </button>
          </FileSection>
        ))}
      </div>
    </Root>
  );
};

const Root = styled.div`
  background-color: ${props => props.theme.grayColorLight};
  padding: 10px 5px 5px 5px;
  width: 100%;
`;

const FileSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 5px;
  margin-bottom: 5px;
  margin-left: 10px;
  padding: 2px 0 2px 10px;
  font-size: 12px;
`;
